'use client';

import { useMotionValue, useSpring, motion, useAnimate, SpringOptions } from 'motion/react';
import { throttle } from 'radash';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { cn } from '@natu/utils/cn';
import { CURSOR_SIZE } from '@natu/utils/consts';

import {
  CustomCursorProviderContextProps,
  CustomCursorProviderProps,
  CustomCursorProviderState,
} from './types';

const CustomCursorContext = createContext<CustomCursorProviderContextProps | undefined>(undefined);

const smoothOptions: SpringOptions = { damping: 20, stiffness: 300, mass: 0.5 };

export const CustomCursorProvider = ({ children }: CustomCursorProviderProps) => {
  const [cursorContent, setCursorContent] = useState<CustomCursorProviderState['content']>('');
  const [scope, animate] = useAnimate();
  const [hideCursor, setHideCursor] = useState(false);
  const [conditionalClassName, setConditionalClassName] = useState<string | null>(null);

  const mousePos = {
    x: useMotionValue(0),
    y: useMotionValue(0),
  };

  const scale = useMotionValue(0);

  const smoothMouse = {
    x: useSpring(mousePos.x, smoothOptions),
    y: useSpring(mousePos.y, smoothOptions),
  };

  useEffect(() => {
    const mouseMove = (e: MouseEvent) => {
      const { clientX, clientY, target } = e;

      setHideCursor(
        (target instanceof HTMLElement &&
          (target.tagName === 'A' || target.tagName === 'BUTTON')) ||
          target instanceof SVGElement,
      );

      // center mouse position
      mousePos.x.set(clientX - CURSOR_SIZE / 2);
      mousePos.y.set(clientY - CURSOR_SIZE / 2);
    };

    const throttledMouseMove = throttle({ interval: 100 }, mouseMove);

    window.addEventListener('mousemove', throttledMouseMove);

    return () => {
      window.removeEventListener('mousemove', throttledMouseMove);
    };
  }, [mousePos.x, mousePos.y]);

  const handleCursorAnimate = useCallback(
    (data: CustomCursorProviderState) => {
      setCursorContent(data.content);

      if (data.variant === 'none') {
        document.body.style.cursor = 'default';
        animate(scope.current, { scale: 0 }, { duration: 0.2 });
      }
      if (data.variant === 'click') {
        document.body.style.cursor = 'none';
        animate(scope.current, { scale: 1 }, { duration: 0.4 });
      }
    },
    [animate, scope],
  );

  const handleConditionalClassName = (className: string | null) => {
    setConditionalClassName(className);
  };

  const contextValue = useMemo(
    () => ({
      handleSetCursorState: handleCursorAnimate,
      handleConditionalClassName,
    }),
    [handleCursorAnimate],
  );

  return (
    <CustomCursorContext.Provider value={contextValue}>
      {children}
      <motion.div
        ref={scope}
        className={cn(
          'bg-primary-600 pointer-events-none fixed left-0 top-0 z-50 hidden h-[106px] w-[106px] origin-center items-center justify-center rounded-full transition-opacity duration-300 lg:flex',
          hideCursor ? 'opacity-0' : conditionalClassName,
        )}
        style={{
          x: smoothMouse.x,
          y: smoothMouse.y,
          scale,
        }}
      >
        {cursorContent}
      </motion.div>
    </CustomCursorContext.Provider>
  );
};

export const useCustomCursorContext = () => {
  const context = useContext(CustomCursorContext);

  if (context === undefined) {
    throw new Error(
      'Hook useCustomCursorContext must be used within a CustomCursorProvider component',
    );
  }

  return context;
};
